/**
 * WCAG contrast helpers for site colours. Each site sets its own colours in
 * Admin; a pale one would make white button text and coloured text on white
 * unreadable, so the theme darkens it just enough to reach AA (4.5:1).
 *
 * Only opaque hex colours are read: #rgb or #rrggbb, with or without the "#"
 * (no alpha, no rgb()/named colours). Anything else makes contrastRatio
 * return NaN and ensureContrast hand the colour back unchanged.
 *
 * ensureContrast only darkens, so it is meant for light backgrounds (the
 * default is white); a colour lighter than its background is left alone.
 */
const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
const AA = 4.5;

function parseHex(color) {
  const match = typeof color === "string" && color.trim().match(HEX);
  if (!match) return null;
  const hex =
    match[1].length === 3 ? [...match[1]].map((c) => c + c).join("") : match[1];
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
}

const toHex = (rgb) =>
  `#${rgb.map((v) => v.toString(16).padStart(2, "0")).join("")}`;

function luminance(rgb) {
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * WCAG contrast ratio (1 to 21) between two hex colours; NaN (never a throw)
 * when either one is not a colour parseHex reads.
 */
export function contrastRatio(a, b) {
  const rgbA = parseHex(a);
  const rgbB = parseHex(b);
  if (!rgbA || !rgbB) return NaN;
  const [hi, lo] = [luminance(rgbA), luminance(rgbB)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * The colour as lowercase #rrggbb, darkened in 10% steps until it reaches
 * `minRatio` on `against`. Returned unchanged (as given) when either colour
 * is not hex, or when it is lighter than `against` (darkening would only
 * lower the contrast).
 */
export function ensureContrast(color, against = "#ffffff", minRatio = AA) {
  let rgb = parseHex(color);
  const background = parseHex(against);
  if (!rgb || !background) return color;
  if (luminance(rgb) > luminance(background)) return color;
  for (
    let i = 0;
    i < 30 && contrastRatio(toHex(rgb), against) < minRatio;
    i++
  ) {
    rgb = rgb.map((v) => Math.round(v * 0.9));
  }
  return toHex(rgb);
}
